import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import bgHero1 from "../assets/hero-01.png";
import bgHero2 from "../assets/hero-02.png";
import bgHero3 from "../assets/hero-03.png";
import { Link } from "react-router-dom";

const slides = [
  {
    tag: "Engineering-Led",
    title: "HVAC Solutions",
    highlight: "in Uganda.",
    sub: ["Design", "Equipment Supply", "Installation", "Maintenance & Repair"],
    bg: bgHero1,
  },
  {
    tag: "World-Class Equipment",
    title: "Premium Brands,",
    highlight: "Unmatched Quality.",
    sub: ["Hisense", "LG", "Samsung", "Trane", "Chiq", "Midea", "Gree"],
    bg: bgHero2,
  },
  {
    tag: "Energy Efficiency",
    title: "Save Energy,",
    highlight: "Stay Comfortable.",
    sub: ["Inverter Technology", "Energy Audits", "Cost-Saving Solutions"],
    bg: bgHero3,
  },
];

const swiperStyles = `
  .hero-swiper .swiper-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    bottom: 16px;
  }
  .hero-swiper .swiper-pagination-bullet {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    background: rgba(255,255,255,0.4);
    color: white;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 600;
    opacity: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    counter-increment: pagination-counter;
  }
  .hero-swiper .swiper-pagination-bullet::after {
    content: counter(pagination-counter);
  }
  .hero-swiper .swiper-pagination {
    counter-reset: pagination-counter;
  }
  .hero-swiper .swiper-pagination-bullet-active {
    background: #00C7EF;
    color: white;
  }
  .hero-swiper .swiper-button-next,
  .hero-swiper .swiper-button-prev {
    color: white;
  }
  .hero-swiper .swiper-button-next::after,
  .hero-swiper .swiper-button-prev::after {
    font-size: 18px;
    font-weight: 700;
  }
  @media (max-width: 640px) {
    .hero-swiper .swiper-button-next,
    .hero-swiper .swiper-button-prev {
      display: none;
    }
  }
`;

// Content animates in fresh on every slide change
// key={activeIndex} forces AnimatePresence to remount and replay animations
function SlideContent({ slide, activeIndex }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeIndex}
        className="font-poppins max-w-xs sm:max-w-md lg:max-w-xl"
      >
        {/* Tag — drops in from top */}
        <motion.p
          className="text-white text-xs sm:text-sm font-semibold uppercase tracking-widest mb-2 sm:mb-3"
          initial={{ opacity: 0, y: -25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
        >
          {slide.tag}
        </motion.p>

        {/* Title — slides in from left */}
        <motion.h1
          className="text-3xl sm:text-4xl lg:text-6xl font-extrabold leading-tight mb-3 sm:mb-4"
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
        >
          {slide.title}<br />
          <span style={{ color: "#00C7EF" }}>{slide.highlight}</span>
        </motion.h1>

        {/* Sub items — fade up */}
        <motion.p
          className="text-white text-xs sm:text-sm mb-6 sm:mb-8 flex flex-wrap items-center gap-1 sm:gap-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut", delay: 0.35 }}
        >
          {slide.sub.map((item, i, arr) => (
            <React.Fragment key={i}>
              <span>{item}</span>
              {i < arr.length - 1 && (
                <span className="text-2xl sm:text-4xl font-bold leading-none" style={{ color: "#00C7EF" }}>•</span>
              )}
            </React.Fragment>
          ))}
        </motion.p>

        {/* Buttons — pop in one by one */}
        <div className="flex flex-wrap gap-3">
          {/* <motion.button
            className="text-white font-bold px-4 sm:px-6 py-2 sm:py-3 text-sm rounded-lg transition-colors shadow-lg hover:opacity-90"
            style={{ backgroundColor: "#00C7EF" }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.5 }}
            whileTap={{ scale: 0.96 }}
          >
            Book Appointment
          </motion.button> */}

          <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: "easeOut", delay: 0.5 }}
              whileTap={{ scale: 0.96 }}
            >
              <Link
                to="/contact?type=appointment"
                className="inline-block text-white font-bold px-4 sm:px-6 py-2 sm:py-3 text-sm rounded-lg transition-colors shadow-lg hover:opacity-90"
                style={{ backgroundColor: "#00C7EF" }}
              >
                Book Appointment
              </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.6 }}
            whileTap={{ scale: 0.96 }}
          >
            <Link
              to="/contact?type=quote"
              className="inline-block border-2 font-bold px-4 sm:px-6 py-2 sm:py-3 text-sm rounded-lg transition-colors hover:opacity-70 bg-transparent text-white"
              style={{ borderColor: "#00C7EF" }}
            >
              Get Quote
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Hero() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="relative">
      <style>{swiperStyles}</style>
      <Swiper
        modules={[Autoplay, Pagination, Navigation]}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        navigation={true}
        loop={true}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className="w-full hero-swiper"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <div className="relative text-white overflow-hidden min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] flex items-center">
              <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${slide.bg})` }}
              />
              <div className="absolute inset-0 bg-blue-900 bg-opacity-40" />
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-32 w-full">
                <SlideContent slide={slide} activeIndex={activeIndex} />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}